import {type StructuredPatch, type StructuredPatchHunk} from 'diff';
import {useMemo} from 'react';

// Counts the number of added and deleted lines in a single hunk.
// A "hunk" in the terminology of a patch, is a contiguous block of changes that
// includes some context lines before and after the actual additions/deletions.
// We need to count additions/deletions at the hunk level in order to provide
// per-patch stats, since the `StructuredPatch` type does not include line-level
// details.
// To do this, we iterate through the `lines` array of the hunk, which contains
// the actual lines of the patch with '+'/'-' prefixes indicating additions and
// deletions, and count how many of each there are.
function hunkStats(hunk: StructuredPatchHunk): [number, number] {
  let adds = 0;
  let dels = 0;
  for (const line of hunk.lines) {
    if (line.startsWith('+')) {
      adds += 1;
    } else if (line.startsWith('-')) {
      dels += 1;
    }
  }
  return [adds, dels];
}

export type PatchStats = {
  adds: number;
  dels: number;
  perPatch: {adds: number; dels: number}[];
};

/**
 * Computes addition/deletion counts for a list of structured patches.
 *
 * @param patches - Array of parsed patches, or `null` if not yet available.
 * @returns An object with total `adds`/`dels` counts and a per-patch breakdown,
 *          or `null` when `patches` is `null`.
 */
export function usePatchStats(
  patches: StructuredPatch[] | null
): PatchStats | null {
  return useMemo(() => {
    if (!patches) {
      return null;
    }
    let totalAdds = 0;
    let totalDels = 0;
    const perPatch = [];
    for (const patch of patches) {
      let patchAdds = 0;
      let patchDels = 0;
      for (const hunk of patch.hunks) {
        const [hunkAdds, hunkDels] = hunkStats(hunk);
        patchAdds += hunkAdds;
        patchDels += hunkDels;
      }
      totalAdds += patchAdds;
      totalDels += patchDels;
      perPatch.push({adds: patchAdds, dels: patchDels});
    }
    return {
      adds: totalAdds,
      dels: totalDels,
      perPatch,
    };
  }, [patches]);
}
